import { useCallback } from 'react';
import { capturePostHogEvent } from '@/hooks/usePostHog';

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

// Event categories for organization
export type AnalyticsEventCategory = 
  | 'auth'
  | 'tribute'
  | 'payment'
  | 'navigation'
  | 'engagement'
  | 'partner'
  | 'plan_ahead'
  | 'pets_page'
  | 'gift'
  | 'collaboration'
  | 'cloud_import'
  | 'ai_generation';

// Predefined events for type safety
export type AnalyticsEvent = 
  // Auth events
  | 'sign_up'
  | 'login'
  | 'logout'
  | 'password_reset'
  // Tribute events
  | 'tribute_created'
  | 'tribute_published'
  | 'tribute_viewed'
  | 'tribute_shared'
  | 'photo_uploaded'
  | 'photo_animated'
  | 'photo_enhanced'
  | 'obituary_generated'
  | 'eulogy_generated'
  | 'video_rendered'
  | 'video_downloaded'
  // Payment events
  | 'plan_selected'
  | 'checkout_started'
  | 'purchase_completed'
  | 'credits_purchased'
  // Gift events
  | 'gift_checkout_started'
  | 'gift_purchase_completed'
  | 'gift_claimed'
  // Engagement events
  | 'cta_clicked'
  | 'feature_explored'
  | 'support_opened'
  | 'donation_made'
  | 'contact_form_submitted'
  // Collaboration events
  | 'collaborator_invited'
  | 'collaborator_accepted'
  // Cloud import events
  | 'cloud_service_connected'
  | 'cloud_photos_imported'
  // Partner events
  | 'partner_applied'
  | 'partner_landing_viewed';

interface EventParams {
  category?: AnalyticsEventCategory;
  label?: string;
  value?: number;
  [key: string]: unknown;
}

export const useAnalytics = () => {
  // Track custom events
  const trackEvent = useCallback((
    eventName: AnalyticsEvent | string,
    params?: EventParams
  ) => {
    // Send to Google Analytics
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', eventName, {
        event_category: params?.category,
        event_label: params?.label,
        value: params?.value,
        ...params,
      });
    }
    
    // Forward to PostHog
    capturePostHogEvent(eventName, {
      category: params?.category,
      label: params?.label,
      value: params?.value,
      ...params,
    });
  }, []);

  // Track page views (useful for SPAs)
  const trackPageView = useCallback((path: string, title?: string) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: path,
        page_title: title || document.title,
      });
    }
  }, []);

  // Track user properties
  const setUserProperties = useCallback((userId?: string, properties?: Record<string, unknown>) => {
    if (typeof window !== 'undefined' && window.gtag) {
      if (userId) {
        window.gtag('config', import.meta.env.VITE_GA_MEASUREMENT_ID || 'G-4HPVE4CV5W', {
          user_id: userId,
        });
      }
      if (properties) {
        window.gtag('set', 'user_properties', properties);
      }
    }
  }, []);

  return {
    trackEvent,
    trackPageView,
    setUserProperties,
  };
};

// Standalone functions for use outside React components
export const trackEvent = (eventName: AnalyticsEvent | string, params?: EventParams) => {
  // Send to Google Analytics
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, {
      event_category: params?.category,
      event_label: params?.label,
      value: params?.value,
      ...params,
    });
  }
  
  // Forward to PostHog
  capturePostHogEvent(eventName, {
    category: params?.category,
    label: params?.label,
    value: params?.value,
    ...params,
  });
};

export const trackPageView = (path: string, title?: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title || document.title,
    });
  }
};
